import { Main } from '@components/Main';
import { PageContent } from '@components/PageContent';
import { PageTitle, PageTitleText } from '@components/PageTitle';
import { TableLinearLoading } from '@components/Table/TableLinearLoading';
import { TableWrapper } from '@components/Table/TableWrapper';
import { useData } from '@hooks/useData';
import { useField } from '@hooks/useField';
import useLoading from '@hooks/useLoading';
import { useValidation } from '@hooks/useValidation';
import { IPagamentoFilter, IPagamentoRecord, IPagamentoSummary, useRelatorioService } from '@services/RelatorioService';
import { yup } from '@validations/Yup';
import React from 'react';
import { PagamentoFilter } from './PagamentoFilter';
import { PagamentoTable } from './PagamentoTable';
import useMessage from '@hooks/useMessage';
import { useValueState } from '@hooks/useValueState';
import { useFormaPagamentoCombo } from '@model/FormaPagamento';
import { useEffectOnce } from '@hooks/useEffectOnce';
import { useEmpresaCombo } from '@model/Empresa';
import { useUsuarioCombo } from '@model/Usuario';
import { useRemetenteWhatsappCombo } from '@model/RemetenteWhatsapp';
import { EnumSortDirection } from '@hooks/useRequest';

export type RelatorioPagamentoProps = {

}

export function RelatorioPagamento (props: RelatorioPagamentoProps) {
    const summaryData = useValueState<IPagamentoSummary>();

    const data = useData<IPagamentoRecord, IPagamentoFilter>({
        filter: {
            competenciaPagamento: useField(),
            competenciaReferencia: useField(),
            formasPagamento: useField([]),
            empresaId: useField(),
            usuarioId: useField(),
            remetenteWhatsapp: useField()
        },

        sortField: {
            name: 'faturaPagamentoId',
            direction: EnumSortDirection.Asc
        }
    });
    
    const loading = useLoading();
    const message = useMessage();
    const relatorioService = useRelatorioService();

    const empresaCombo = useEmpresaCombo();
    const formaPagamentoCombo = useFormaPagamentoCombo();
    const usuarioCombo = useUsuarioCombo();
    const remetenteWhatsappCombo = useRemetenteWhatsappCombo();

    const validation = useValidation<IPagamentoFilter>({
        competenciaPagamento: yup.string().required(),
    })

    const handleCsvButtonClick = React.useCallback(() => {
        if (!loading.isLoading) {
            validation.validate(data.requestedData.filter)
                .then(() => {
                    loading.start();
                    relatorioService.exportarPagamentos(data.requestedData)
                    .catch(message.showBlobException)
                    .finally(loading.stop);
                })
                .catch(data.filter.setErrorsFromYup);
            }
    }, [data.filter.setErrorsFromYup, data.requestedData, loading, message.showBlobException, relatorioService, validation]);

    React.useEffect(() => {
        if (!loading.isLoading && data.requestedData.filter.competenciaPagamento !== undefined) {
            validation.validate(data.requestedData.filter)
                .then(() => {
                    loading.start();
                    relatorioService.listarPagamentos(data.requestedData)
                        .then(response => {
                            data.updateDataResponse(response.data.data);
                            summaryData.set(response.data.summary);
                        })
                        .catch(message.showException)
                        .finally(loading.stop);
                })
                .catch(data.filter.setErrorsFromYup);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.requestedData]);

    useEffectOnce(() => {
        empresaCombo.loadOptions();
        formaPagamentoCombo.loadManuaisOptions();
        usuarioCombo.loadInternos();
        remetenteWhatsappCombo.loadOptions();
    })

    return (
        <React.Fragment>
            <Main>
                <PageTitle>
                    <PageTitleText text="Relatório de Pagamentos" />
                </PageTitle>

                <PageContent>
                    <PagamentoFilter
                        filter={data.filter}
                        isLoading={loading.isLoading}
                        empresaCombo={empresaCombo}
                        formaPagamentoCombo={formaPagamentoCombo}
                        usuarioCombo={usuarioCombo}
                        remetenteWhatsappCombo={remetenteWhatsappCombo}
                    />

                    <TableWrapper>
                        <TableLinearLoading isLoading={loading.isLoading} />
                        <PagamentoTable
                            data={data}
                            isLoading={loading.isLoading}
                            onCsvButtonClick={handleCsvButtonClick}
                        />
                    </TableWrapper>
                </PageContent>
            </Main>
        </React.Fragment>
    )
}